/**
 * BACKLINK FIRSAT AJANI — link/anılma kazanılacak yerler, iki cephe:
 *   SEO: hedef kelimelerinin SERP'lerinde sıralanan HABER/MEDYA siteleri
 *        ("bu siteden sponsorlu içerik/PR ile hem link hem o trafik alınır")
 *   GEO: yapay zekâ cevaplarında KAYNAK gösterilen domainler
 *        ("AI'ın güvendiği yerde anıl, AI cevaplarına gir")
 * + rakip-özel araçlar: linkbait ve kırık sayfa avı (DFS ücretli, elle tetik).
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { Link2, Newspaper, Sparkles, Loader2, ExternalLink, Search } from "lucide-react";
import { BacklinkAvlari } from "@/components/backlink-avlar";

const C = { green: "#12b76a", red: "#d92d20", amber: "#f79009", purple: "#7a5af8", blue: "#2970ff" };

type SeoFirsat = { domain: string; haberMi: boolean; kelimeSayisi: number; enIyiPozisyon: number; ornekler: { keyword: string; position: number; url: string; title: string }[] };
type GeoFirsat = { domain: string; soruSayisi: number; aiHacimToplam: number; ornekSorular: string[]; haberMi: boolean };
type NewsFirsat = { domain: string; haberSayisi: number; ornekler: { keyword: string; title: string; url: string; kaynak: string }[] };

const PAZARLAR = [["tr", "🇹🇷 Türkiye"], ["en", "🇺🇸 ABD"], ["en-gb", "🇬🇧 İngiltere"], ["de", "🇩🇪 Almanya"], ["fr", "🇫🇷 Fransa"], ["es", "🇪🇸 İspanya"], ["it", "🇮🇹 İtalya"], ["ar", "🇸🇦 Arapça"], ["ru", "🇷🇺 Rusya"], ["nl", "🇳🇱 Hollanda"]] as const;

export default function BacklinkFirsat() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const [cephe, setCephe] = useState<"seo" | "geo" | "av">("seo");
  // Elle SERP taraması — SEO uzmanı modu: hedef pazar + ilk 100'e kadar derinlik;
  // organikte haber çıkmazsa sunucu Google News sekmesine de bakar (haberNews).
  const [elleKelime, setElleKelime] = useState("");
  const [ellePazar, setEllePazar] = useState("tr");
  const [elleDerinlik, setElleDerinlik] = useState(100);
  const [elleYukleniyor, setElleYukleniyor] = useState(false);
  const [elleSonuc, setElleSonuc] = useState<null | { taranan: number; veriGelen: number; haber: SeoFirsat[]; diger: SeoFirsat[]; haberNews: NewsFirsat[] }>(null);
  const elleTara = async () => {
    const kelimeler = elleKelime.split(/[,\n]/).map((k) => k.trim()).filter(Boolean);
    if (!kelimeler.length || elleYukleniyor || !activeDomainId) return;
    setElleYukleniyor(true);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/backlink-firsat/seo/tara`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ keywords: kelimeler, market: ellePazar, depth: elleDerinlik }),
      });
      const j = await r.json();
      if (!r.ok) { alert(j.error ?? "Tarama başarısız"); return; }
      setElleSonuc(j);
    } catch { alert("Bağlantı hatası"); }
    finally { setElleYukleniyor(false); }
  };

  // GEO elle tarama: kelimenin CANLI AI Overview kaynakları ("AI şu an kime güveniyor?")
  const [geoKelime, setGeoKelime] = useState("");
  const [geoPazar, setGeoPazar] = useState("tr");
  const [geoTaraYukleniyor, setGeoTaraYukleniyor] = useState(false);
  const [geoTaraSonuc, setGeoTaraSonuc] = useState<null | { taranan: number; firsatlar: GeoFirsat[]; aioYok: string[] }>(null);
  const geoTara = async () => {
    const kelimeler = geoKelime.split(/[,\n]/).map((k) => k.trim()).filter(Boolean);
    if (!kelimeler.length || geoTaraYukleniyor || !activeDomainId) return;
    setGeoTaraYukleniyor(true);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/backlink-firsat/geo/tara`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ keywords: kelimeler, market: geoPazar }),
      });
      const j = await r.json();
      if (!r.ok) { alert(j.error ?? "Tarama başarısız"); return; }
      setGeoTaraSonuc(j);
    } catch { alert("Bağlantı hatası"); }
    finally { setGeoTaraYukleniyor(false); }
  };

  const { data: seo, isLoading: seoYukleniyor } = useQuery<{ kelimeSayisi: number; haber: SeoFirsat[]; diger: SeoFirsat[] }>({
    queryKey: ["bf-seo", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async () => (await fetch(`/api/domains/${activeDomainId}/backlink-firsat/seo`)).json(),
  });
  const { data: geo, isLoading: geoYukleniyor } = useQuery<{ veriVar: boolean; firsatlar: GeoFirsat[] }>({
    queryKey: ["bf-geo", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async () => (await fetch(`/api/domains/${activeDomainId}/backlink-firsat/geo`)).json(),
  });

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `${C.blue}15` }}><Link2 className="h-6 w-6" style={{ color: C.blue }} /></div>
        <div>
          <h1 className="text-xl font-bold">Backlink Fırsat Ajanı</h1>
          <p className="text-sm text-muted-foreground">Link ve anılma nereden kazanılır: SEO cephesinde kelimelerinde sıralanan haber siteleri, GEO cephesinde AI'ın kaynak gösterdiği yerler.</p>
        </div>
      </div>

      {/* Cephe seçimi */}
      <div className="mb-4 flex w-fit items-center gap-1 rounded-xl bg-gray-100 p-1 dark:bg-white/[0.06]">
        {([["seo", "SEO Fırsatları", Newspaper], ["geo", "GEO Fırsatları (AI)", Sparkles], ["av", "Rakip Avları", Link2]] as const).map(([k, ad, Ikon]) => (
          <button key={k} onClick={() => setCephe(k)}
            className={`flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-sm font-medium transition ${cephe === k ? "bg-white text-gray-800 shadow-sm dark:bg-gray-800 dark:text-white" : "text-gray-500 hover:text-gray-700 dark:text-gray-400"}`}>
            <Ikon className="h-4 w-4" /> {ad}
          </button>
        ))}
      </div>

      {cephe === "av" ? (
        activeDomainId ? <BacklinkAvlari domainId={activeDomainId} /> : null
      ) : cephe === "seo" ? (
        <Card><CardContent className="p-4">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold"><Newspaper className="h-4 w-4" style={{ color: C.amber }} /> Kelimelerinde Sıralanan Haber/Medya Siteleri</div>
          <p className="mb-3 rounded-lg bg-gray-50 px-3 py-2 text-[12px] leading-relaxed text-muted-foreground dark:bg-white/[0.03]">Takip ettiğin kelimelerin gerçek Google sonuçlarında (gece koşularından) çıkan haber ve medya siteleri. Bu sitelerden sponsorlu içerik/PR ile hem <b>backlink</b> hem de o sıralamanın <b>hazır trafiği</b> alınabilir — link satın almanın en meşru hali.</p>
          {/* ELLE TARAMA — takipte olmayan kelimeler için anlık SERP çekimi (ücretli, elle tetik) */}
          <div className="mb-3 rounded-lg border border-dashed border-gray-200 p-3 dark:border-gray-700">
            <div className="mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold"><Search className="h-3.5 w-3.5" /> Kelime ekleyerek tara <span className="font-normal text-muted-foreground">— takip listende olmayan kelimelerin SERP'ine bakar (en çok 10, DFS ücretli)</span></div>
            <div className="flex flex-wrap gap-2">
              <input value={elleKelime} onChange={(e) => setElleKelime(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void elleTara(); }}
                placeholder="virgülle ayır: bbl istanbul, burun estetiği fiyat, …" spellCheck={false}
                className="h-9 min-w-[260px] flex-1 rounded-lg border border-gray-200 bg-transparent px-3 text-[13px] outline-none focus:border-brand-300 dark:border-gray-700 dark:text-white/90 dark:placeholder:text-gray-500" />
              <select value={ellePazar} onChange={(e) => setEllePazar(e.target.value)} title="Hedef pazar — SERP bu ülke/dilden çekilir"
                className="h-9 rounded-lg border border-gray-200 bg-white px-2 text-[12.5px] outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white/90">
                {PAZARLAR.map(([v, ad]) => <option key={v} value={v}>{ad}</option>)}
              </select>
              <select value={elleDerinlik} onChange={(e) => setElleDerinlik(Number(e.target.value))} title="Kaç sonuca kadar bakılsın"
                className="h-9 rounded-lg border border-gray-200 bg-white px-2 text-[12.5px] outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white/90">
                {[20, 50, 100].map((d2) => <option key={d2} value={d2}>İlk {d2}</option>)}
              </select>
              <Button size="sm" className="h-9" disabled={elleYukleniyor || !elleKelime.trim()} onClick={() => void elleTara()}>
                {elleYukleniyor ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}{elleYukleniyor ? "Taranıyor…" : "SERP'leri Tara"}
              </Button>
            </div>
            {elleSonuc && (
              <div className="mt-2.5 space-y-2">
                <p className="text-[11.5px] text-muted-foreground">{elleSonuc.taranan} kelime tarandı ({elleSonuc.veriGelen} sonuçlu) — organikte {elleSonuc.haber.length}, Google News'te {(elleSonuc.haberNews ?? []).length} haber/medya sitesi bulundu{elleSonuc.haber.length + (elleSonuc.haberNews?.length ?? 0) === 0 ? " (ikisinde de yok)" : ":"}</p>
                {(elleSonuc.haberNews ?? []).map((n) => (
                  <div key={"news-" + n.domain} className="rounded-lg border border-gray-100 p-2.5 dark:border-gray-800">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <a href={`https://${n.domain}`} target="_blank" rel="noopener noreferrer" className="text-[13px] font-semibold text-brand-600 hover:underline">{n.domain}</a>
                      <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: `${C.red}15`, color: C.red }}>GOOGLE NEWS</span>
                      <span className="text-[11px] text-muted-foreground">{n.haberSayisi} haber içeriği</span>
                    </div>
                    {n.ornekler.map((o) => (
                      <div key={o.url} className="mt-1 flex items-center gap-2 text-[11.5px] text-muted-foreground">
                        <span className="shrink-0 font-medium text-foreground/80">{o.keyword}</span>
                        <a href={o.url} target="_blank" rel="noopener noreferrer" className="truncate hover:underline" title={o.title}>{o.title || o.url}</a>
                      </div>
                    ))}
                  </div>
                ))}
                {elleSonuc.haber.map((h) => (
                  <div key={"elle-" + h.domain} className="rounded-lg border border-gray-100 p-2.5 dark:border-gray-800">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <a href={`https://${h.domain}`} target="_blank" rel="noopener noreferrer" className="text-[13px] font-semibold text-brand-600 hover:underline">{h.domain}</a>
                      <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: `${C.amber}18`, color: C.amber }}>HABER/MEDYA</span>
                      <span className="text-[11px] text-muted-foreground">{h.kelimeSayisi} kelimede · en iyi #{h.enIyiPozisyon}</span>
                    </div>
                    {h.ornekler.slice(0, 3).map((o) => (
                      <div key={o.url + o.keyword} className="mt-1 flex items-center gap-2 text-[11.5px] text-muted-foreground">
                        <span className="w-7 shrink-0 text-right tabular-nums">#{o.position}</span>
                        <span className="shrink-0 font-medium text-foreground/80">{o.keyword}</span>
                        <a href={o.url} target="_blank" rel="noopener noreferrer" className="truncate hover:underline" title={o.title}>{o.title || o.url}</a>
                      </div>
                    ))}
                  </div>
                ))}
                {elleSonuc.diger.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {elleSonuc.diger.slice(0, 20).map((d2) => <span key={"elle-d-" + d2.domain} className="rounded-full border border-gray-200 px-2 py-0.5 text-[11px] dark:border-gray-700" title={`${d2.kelimeSayisi} kelimede · en iyi #${d2.enIyiPozisyon}`}>{d2.domain}</span>)}
                  </div>
                )}
              </div>
            )}
          </div>
          {seoYukleniyor ? (
            <div className="py-10 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />SERP verileri taranıyor…</div>
          ) : !seo || seo.kelimeSayisi === 0 ? (
            <p className="text-[12.5px] text-muted-foreground">Takipte kelime yok — önce Sıralama Takibi'ne kelime ekle; fırsatlar gece koşularından beslenir.</p>
          ) : seo.haber.length === 0 ? (
            <div className="space-y-2">
              <p className="text-[12.5px] text-muted-foreground">{seo.kelimeSayisi} kelimenin SERP'lerinde haber/medya sitesi görünmüyor. Aşağıdaki listede sıralanan siteler yine de PR/anılma hedefi olabilir.</p>
              {seo.diger.length > 0 && (
                <details className="pt-1" open>
                  <summary className="cursor-pointer text-[12px] font-medium text-muted-foreground hover:text-foreground">Diğer sıralanan siteler ({seo.diger.length}) — rakipler ve olası PR hedefleri</summary>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {seo.diger.map((d2) => <span key={d2.domain} className="rounded-full border border-gray-200 px-2 py-0.5 text-[11.5px] dark:border-gray-700" title={`${d2.kelimeSayisi} kelime · en iyi #${d2.enIyiPozisyon}`}>{d2.domain}</span>)}
                  </div>
                </details>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {seo.haber.map((h) => (
                <div key={h.domain} className="rounded-lg border border-gray-100 p-3 dark:border-gray-800">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <a href={`https://${h.domain}`} target="_blank" rel="noopener noreferrer" className="font-semibold text-brand-600 hover:underline">{h.domain}</a>
                    <span className="rounded-full px-2 py-0.5 text-[10.5px] font-bold" style={{ background: `${C.amber}18`, color: C.amber }}>HABER/MEDYA</span>
                    <span className="text-[11.5px] text-muted-foreground">{h.kelimeSayisi} kelimende sıralanıyor · en iyi #{h.enIyiPozisyon}</span>
                  </div>
                  <div className="mt-1.5 space-y-0.5">
                    {h.ornekler.map((o) => (
                      <div key={o.url + o.keyword} className="flex items-center gap-2 text-[12px] text-muted-foreground">
                        <span className="w-8 shrink-0 text-right tabular-nums">#{o.position}</span>
                        <span className="shrink-0 font-medium text-foreground/80">{o.keyword}</span>
                        <a href={o.url} target="_blank" rel="noopener noreferrer" className="truncate hover:underline" title={o.title}>{o.title || o.url}</a>
                        <ExternalLink className="h-3 w-3 shrink-0 opacity-40" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {seo.diger.length > 0 && (
                <details className="pt-1">
                  <summary className="cursor-pointer text-[12px] font-medium text-muted-foreground hover:text-foreground">Diğer sıralanan siteler ({seo.diger.length}) — rakipler ve olası PR hedefleri</summary>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {seo.diger.map((d2) => <span key={d2.domain} className="rounded-full border border-gray-200 px-2 py-0.5 text-[11.5px] dark:border-gray-700" title={`${d2.kelimeSayisi} kelime · en iyi #${d2.enIyiPozisyon}`}>{d2.domain}</span>)}
                  </div>
                </details>
              )}
            </div>
          )}
        </CardContent></Card>
      ) : (
        <Card><CardContent className="p-4">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold"><Sparkles className="h-4 w-4" style={{ color: C.purple }} /> AI'ın Kaynak Gösterdiği Domainler</div>
          <p className="mb-3 rounded-lg bg-gray-50 px-3 py-2 text-[12px] leading-relaxed text-muted-foreground dark:bg-white/[0.03]">Senin konularındaki AI Overview cevaplarında <b>kaynak gösterilen</b> siteler — yapay zekânın güvendiği yerler. Bu domainlerde anılmak/link almak (misafir yazı, röportaj, dizin kaydı, PR), AI cevaplarına girmenin en kısa yoludur.</p>
          {/* ELLE TARAMA — kelimenin CANLI AI Overview kaynakları, hedef pazar seçimiyle */}
          <div className="mb-3 rounded-lg border border-dashed border-gray-200 p-3 dark:border-gray-700">
            <div className="mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold"><Search className="h-3.5 w-3.5" /> Kelime/soru ekleyerek tara <span className="font-normal text-muted-foreground">— AI Overview şu an kimi kaynak gösteriyor (en çok 10, DFS ücretli)</span></div>
            <div className="flex flex-wrap gap-2">
              <input value={geoKelime} onChange={(e) => setGeoKelime(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void geoTara(); }}
                placeholder="virgülle ayır: bbl nedir, brazilian butt lift recovery, …" spellCheck={false}
                className="h-9 min-w-[260px] flex-1 rounded-lg border border-gray-200 bg-transparent px-3 text-[13px] outline-none focus:border-brand-300 dark:border-gray-700 dark:text-white/90 dark:placeholder:text-gray-500" />
              <select value={geoPazar} onChange={(e) => setGeoPazar(e.target.value)} title="Hedef pazar — AI Overview bu ülke/dilden sorgulanır"
                className="h-9 rounded-lg border border-gray-200 bg-white px-2 text-[12.5px] outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white/90">
                {PAZARLAR.map(([v, ad]) => <option key={v} value={v}>{ad}</option>)}
              </select>
              <Button size="sm" className="h-9" disabled={geoTaraYukleniyor || !geoKelime.trim()} onClick={() => void geoTara()}>
                {geoTaraYukleniyor ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}{geoTaraYukleniyor ? "Taranıyor…" : "AI Kaynaklarını Tara"}
              </Button>
            </div>
            {geoTaraSonuc && (
              <div className="mt-2.5 space-y-2">
                <p className="text-[11.5px] text-muted-foreground">
                  {geoTaraSonuc.taranan} kelime tarandı — {geoTaraSonuc.firsatlar.length ? `${geoTaraSonuc.firsatlar.length} kaynak domain bulundu:` : "AI Overview kaynağı bulunamadı."}
                  {geoTaraSonuc.aioYok.length > 0 && ` Şu kelimelerde AI cevabı çıkmıyor (fırsat: ilk cevap sen olabilirsin): ${geoTaraSonuc.aioYok.join(", ")}.`}
                </p>
                {geoTaraSonuc.firsatlar.map((g) => (
                  <div key={"gt-" + g.domain} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-lg border border-gray-100 p-2.5 dark:border-gray-800">
                    <a href={`https://${g.domain}`} target="_blank" rel="noopener noreferrer" className="text-[13px] font-semibold text-brand-600 hover:underline">{g.domain}</a>
                    {g.haberMi && <span className="rounded-full px-1.5 py-0.5 text-[10px] font-bold" style={{ background: `${C.amber}18`, color: C.amber }}>MEDYA</span>}
                    <span className="text-[11px] text-muted-foreground">{g.soruSayisi} kelimede kaynak · {g.ornekSorular.join(" · ")}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {geoYukleniyor ? (
            <div className="py-10 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />AI kaynakları taranıyor…</div>
          ) : !geo?.veriVar ? (
            <p className="text-[12.5px] text-muted-foreground">Henüz AI Overview koşusu yok — Sıralama Takibi'nden bir AI görünürlük koşusu başlat; kaynak haritası oradan çıkar.</p>
          ) : geo.firsatlar.length === 0 ? (
            <p className="text-[12.5px] text-muted-foreground">Kaynak gösterilen dış domain bulunamadı.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] border-collapse text-[12.5px]">
                <thead><tr className="border-b-2 border-gray-100 text-left dark:border-gray-800">
                  <th className="px-2 py-2 font-semibold">Domain</th><th className="px-2 py-2 text-center">Kaç soruda kaynak</th><th className="px-2 py-2 text-center">Aylık AI arama (toplam)</th><th className="px-2 py-2">Örnek sorular</th>
                </tr></thead>
                <tbody>
                  {geo.firsatlar.map((g) => (
                    <tr key={g.domain} className="border-b border-gray-50 align-top dark:border-gray-800/60">
                      <td className="px-2 py-2"><a href={`https://${g.domain}`} target="_blank" rel="noopener noreferrer" className="font-medium text-brand-600 hover:underline">{g.domain}</a>{g.haberMi && <span className="ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold" style={{ background: `${C.amber}18`, color: C.amber }}>MEDYA</span>}</td>
                      <td className="px-2 py-2 text-center tabular-nums">{g.soruSayisi}</td>
                      <td className="px-2 py-2 text-center tabular-nums">{g.aiHacimToplam ? g.aiHacimToplam.toLocaleString("tr-TR") : "—"}</td>
                      <td className="max-w-[340px] px-2 py-2 text-[11.5px] text-muted-foreground">{g.ornekSorular.join(" · ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent></Card>
      )}

      <p className="mt-3 text-[11px] text-muted-foreground">Rakip-özel avlar (link mıknatısı içerikler + kırık ama backlink'li sayfalar) yukarıdaki "Rakip Avları" sekmesinde — rakip domain girerek çalışır.</p>
    </div>
  );
}
